import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AddEmployee from './AddEmployee';
import EditEmployee from './EditEmployee';
import ImportEmployeeModal from './ImportEmployeeModal';
import axios from 'axios';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('name');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    can_export: false,
    can_import: false,
    loaded: false
  });

  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE;
  const API_BASE_1 = process.env.REACT_APP_API_BASE_1;

  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        // Find permissions for Employees module
        const employeePermissions = response.data.permissions.find(
          p => p.module_name === 'Employees'
        );

        if (employeePermissions) {
          setUserPermissions({
            can_view: Boolean(employeePermissions.can_view),
            can_add: Boolean(employeePermissions.can_add),
            can_edit: Boolean(employeePermissions.can_edit),
            can_delete: Boolean(employeePermissions.can_delete),
            can_export: Boolean(employeePermissions.can_export),
            can_import: Boolean(employeePermissions.can_import),
            loaded: true
          });
        } else {
          // If no employee permissions found, set all to false
          setUserPermissions({
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_export: false,
            can_import: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          loaded: true
        });
      }
    } catch (error) {
      setUserPermissions({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        can_import: false,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/employees.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setEmployees(response.data.employees);
      }
    } catch (error) {
      setError('Failed to fetch employees');
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      await fetchEmployees();
    };

    initializeData();
  }, [fetchUserPermissions, fetchEmployees]);

  const handleDelete = async (empId) => {
    if (!userPermissions.can_delete) {
      alert('You do not have permission to delete employees');
      return;
    }

    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_employee.php`,
          { emp_id: empId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchEmployees();
        } else {
          setError(response.data.error || 'Failed to delete employee');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete employee');
        console.error('Error deleting employee:', error);
      }
    }
  };

  const handleEdit = (employee) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to edit employees');
      return;
    }
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleViewDetails = (employee) => {
    if (!userPermissions.can_view) {
      alert('You do not have permission to view employee details');
      return;
    }
    alert(`Viewing details for: ${employee.first_name} ${employee.last_name}\nEmail: ${employee.email}\nStatus: ${employee.status}`);
  };

  const handleQuickAction = (employee, action) => {
    if (!userPermissions.can_view) {
      alert('You do not have permission to perform this action');
      return;
    }

    switch (action) {
      case 'email':
        window.location.href = `mailto:${employee.email}`;
        break;
      case 'call':
        if (employee.phone) {
          window.location.href = `tel:${employee.phone}`;
        } else {
          alert('No phone number available');
        }
        break;
      case 'message':
        alert(`Send message to ${employee.first_name}`);
        break;
      default:
        break;
    }
  };

  // Import function
  const handleImportEmployees = async (records, setProgress) => {
    try {
      const token = localStorage.getItem('authToken');
      const totalRecords = records.length;
      let processed = 0;

      // Process in batches of 10 to show progress
      const batchSize = 10;
      for (let i = 0; i < totalRecords; i += batchSize) {
        const batch = records.slice(i, i + batchSize);

        const response = await axios.post(
          `${API_BASE}imports/import_employee.php`,
          { records: batch },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.data.success) {
          throw new Error(response.data.error || 'Import failed');
        }

        processed += batch.length;
        const progress = Math.round((processed / totalRecords) * 100);
        setProgress(progress);
      }

      // Refresh employees after import
      await fetchEmployees();
    } catch (error) {
      throw error;
    }
  };

  // Navigation handlers
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToHome = () => {
    navigate('/');
  };

  const goToCustomFields = () => {
    if (!userPermissions.can_view) {
      alert('You do not have permission to access custom fields');
      return;
    }
    navigate('/custom-fields');
  };

  const goToAttendance = () => {
    navigate('/attendance');
  };

  const goToUserManagement = () => {
    navigate('/user-management');
  };

  const goToEmployeePrint = () => {
    navigate('/employee-print');
  };

  // Filter employees based on search and status
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All' || employee.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Sort employees
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      case 'date':
        return new Date(b.doj) - new Date(a.doj);
      case 'department':
        return (a.dept_name || '').localeCompare(b.dept_name || '');
      default:
        return 0;
    }
  });

  // Function to check if photo path is valid and construct full URL
  const getEmployeePhoto = (employee) => {
    if (employee.photo && employee.photo.trim() !== '') {
      if (employee.photo.startsWith('http')) {
        return employee.photo;
      }
      return `${API_BASE_1}/${employee.photo}`;
    }
    return null;
  };

  // Permission check functions
  const canAddEmployee = () => userPermissions.can_add;
  const canEditEmployee = () => userPermissions.can_edit;
  const canDeleteEmployee = () => userPermissions.can_delete;
  const canViewEmployees = () => userPermissions.can_view;
  const canExportEmployees = () => userPermissions.can_export;
  const canImportEmployees = () => userPermissions.can_import;

  // Show loading while permissions are being fetched
  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have view permission, show access denied
  if (!canViewEmployees()) {
    return (
      <div className="container-fluid py-4 youtube-page-container">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view employees.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Enhanced Header with Navigation Options */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-people me-2 text-primary"></i>
                        Employee Management
                        {!canAddEmployee() && !canEditEmployee() && !canDeleteEmployee() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage your organization's employees efficiently
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="btn-group me-2" role="group">
                      <button
                        className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="List View"
                      >
                        <i className="bi bi-list-ul"></i>
                      </button>
                      <button
                        className={`btn btn-outline-primary ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                      >
                        <i className="bi bi-grid-3x3-gap"></i>
                      </button>
                    </div>
                    <div className="btn-group me-2" role="group">
                      <button
                        className="btn btn-outline-info"
                        onClick={goToCustomFields}
                        title="Manage Custom Fields"
                      >
                        <i className="bi bi-list-check me-2"></i>
                        Custom Fields
                      </button>
                      {canExportEmployees() && (
                        <button
                          className="btn btn-outline-secondary"
                          onClick={goToEmployeePrint}
                          title="Print & Export Employees"
                        >
                          <i className="bi bi-printer me-2"></i>
                          Export
                        </button>
                      )}
                    </div>
                    {canAddEmployee() && (
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Add Employee
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                    title="Go to Dashboard"
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    disabled
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={goToAttendance}
                    title="Go to Attendance"
                  >
                    <i className="bi bi-calendar-check me-2"></i>
                    Attendance
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    onClick={goToUserManagement}
                    title="Go to User Management"
                  >
                    <i className="bi bi-person-gear me-2"></i>
                    User Management
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Resigned">Resigned</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="date">Sort by Join Date</option>
                    <option value="department">Sort by Department</option>
                  </select>
                </div>
                <div className="col-md-2 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={fetchEmployees}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                  {canImportEmployees() && (
                    <button
                      className="btn btn-outline-success ms-2"
                      onClick={() => setShowImportModal(true)}
                      title="Import Employees"
                    >
                      <i className="bi bi-upload"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Employees Display */}
      {viewMode === 'list' ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-0">
                {sortedEmployees.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-people display-1 text-muted"></i>
                    <h4 className="mt-3 text-muted">No employees found</h4>
                    <p className="text-muted">
                      {employees.length === 0
                        ? "Get started by adding your first employee."
                        : "No employees match your search criteria."
                      }
                    </p>
                    {employees.length === 0 && canAddEmployee() && (
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className="bi bi-person-plus me-2"></i>
                        Add First Employee
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4">Employee</th>
                          <th>Code</th>
                          <th>Department</th>
                          <th>Designation</th>
                          <th>Contact</th>
                          <th>Join Date</th>
                          <th>Status</th>
                          <th className="text-end pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedEmployees.map((employee) => {
                          const photoUrl = getEmployeePhoto(employee);
                          return (
                            <tr key={employee.emp_id} className="employee-row">
                              <td className="ps-4">
                                <div className="d-flex align-items-center">
                                  {photoUrl ? (
                                    <img
                                      src={photoUrl}
                                      alt={`${employee.first_name} ${employee.last_name}`}
                                      className="employee-avatar rounded-circle me-3"
                                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`employee-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 ${photoUrl ? 'd-none' : ''}`}
                                    style={{ width: '40px', height: '40px' }}
                                  >
                                    {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="fw-semibold">
                                      {employee.first_name} {employee.last_name}
                                    </div>
                                    <small className="text-muted">{employee.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {employee.emp_code}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {employee.dept_name || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {employee.desig_name || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <small className="text-muted">
                                    {employee.phone || 'No phone'}
                                  </small>
                                  {employee.email && (
                                    <button
                                      className="btn btn-sm btn-outline-primary mt-1"
                                      onClick={() => handleQuickAction(employee, 'email')}
                                      title="Send Email"
                                    >
                                      <i className="bi bi-envelope"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {employee.doj ? (() => {
                                    const date = new Date(employee.doj);
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const year = date.getFullYear();
                                    return `${day}/${month}/${year}`;
                                  })() : 'N/A'}
                                </small>
                              </td>
                              <td>
                                <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                                  employee.status === 'Inactive' ? 'bg-warning' : 'bg-danger'
                                  }`}>
                                  {employee.status}
                                </span>
                              </td>
                              <td className="text-end pe-4">
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-outline-info btn-sm"
                                    onClick={() => handleViewDetails(employee)}
                                    title="View Details"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  {canEditEmployee() && (
                                    <button
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => handleEdit(employee)}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                  )}
                                  {canDeleteEmployee() && (
                                    <button
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDelete(employee.emp_id)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          {sortedEmployees.length === 0 ? (
            <div className="col-12">
              <div className="text-center py-5">
                <i className="bi bi-people display-1 text-muted"></i>
                <h4 className="mt-3 text-muted">No employees found</h4>
                <p className="text-muted">
                  {employees.length === 0
                    ? "Get started by adding your first employee."
                    : "No employees match your search criteria."
                  }
                </p>
              </div>
            </div>
          ) : (
            sortedEmployees.map((employee) => {
              const photoUrl = getEmployeePhoto(employee);
              return (
                <div key={employee.emp_id} className="col-md-6 col-lg-4 col-xl-3 mb-4">
                  <div className="card employee-card shadow-sm border-0 h-100">
                    <div className="card-body text-center">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={`${employee.first_name} ${employee.last_name}`}
                          className="employee-avatar-lg rounded-circle mx-auto mb-3"
                          style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`employee-avatar-lg bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 ${photoUrl ? 'd-none' : ''}`}
                        style={{ width: '80px', height: '80px' }}
                      >
                        {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                      </div>

                      <h5 className="card-title fw-bold mb-1">
                        {employee.first_name} {employee.last_name}
                      </h5>
                      <p className="text-muted small mb-2">{employee.emp_code}</p>
                      <p className="card-text small text-muted mb-2">
                        <i className="bi bi-building me-1"></i>
                        {employee.dept_name || 'No Department'}
                      </p>
                      <p className="card-text small text-muted mb-3">
                        <i className="bi bi-briefcase me-1"></i>
                        {employee.desig_name || 'No Designation'}
                      </p>
                      <div className="mb-3">
                        <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                          employee.status === 'Inactive' ? 'bg-warning' : 'bg-danger'
                          }`}>
                          {employee.status}
                        </span>
                      </div>
                      <div className="btn-group w-100" role="group">
                        <button
                          className="btn btn-outline-info btn-sm"
                          onClick={() => handleViewDetails(employee)}
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        {canEditEmployee() && (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEdit(employee)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        <button
                          className="btn btn-outline-success btn-sm"
                          onClick={() => handleQuickAction(employee, 'email')}
                          title="Send Email"
                        >
                          <i className="bi bi-envelope"></i>
                        </button>
                        {canDeleteEmployee() && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(employee.emp_id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{employees.length}</div>
                  <small className="text-muted">Total Employees</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {employees.filter(e => e.status === 'Active').length}
                  </div>
                  <small className="text-muted">Active</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {employees.filter(e => e.status === 'Inactive').length}
                  </div>
                  <small className="text-muted">Inactive</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-danger fs-4">
                    {employees.filter(e => e.status === 'Resigned').length}
                  </div>
                  <small className="text-muted">Resigned</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                  title="Back to Dashboard"
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={goToCustomFields}
                >
                  <i className="bi bi-list-check me-2"></i>
                  Custom Fields
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={goToUserManagement}
                  title="Go to User Management"
                >
                  <i className="bi bi-person-gear me-2"></i>
                  User Management
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddEmployee
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onEmployeeAdded={fetchEmployees}
        />
      )}

      {showEditModal && selectedEmployee && (
        <EditEmployee
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          employee={selectedEmployee}
          onEmployeeUpdated={fetchEmployees}
        />
      )}

      {showImportModal && (
        <ImportEmployeeModal
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onImport={handleImportEmployees}
          canImportEmployees={canImportEmployees}
          error={error}
          setError={setError}
        />
      )}
    </div>
  );
};

export default Employees;